import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Baby, Sparkle, LockKey, Info, Moon } from "@phosphor-icons/react";
import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import PaywallGate from "../components/PaywallGate";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import { WindowCard, DayStrip, MotherSection, SectionTitle, Sun, ExclusionsPanel } from "../components/babyMuhurat/Sections";
import { SlotChartDialog } from "../components/babyMuhurat/SlotChartDialog";

const TOOL = "baby_muhurat";
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const inputCls = "mt-1 w-full rounded-xl border border-amber-200 bg-[#FFFBF3] px-3 py-2.5 text-sm";

function Field({ label, children }) {
  return <label className="block text-xs font-semibold text-[#3F2D1B]/70">{label}<div className="mt-0.5">{children}</div></label>;
}

function MotherFields({ m, setM }) {
  return (
    <div className="grid sm:grid-cols-3 gap-3 mt-3" data-testid="baby-mother-fields">
      <Field label="Mother's date of birth"><input type="date" value={m.date} onChange={(e) => setM({ ...m, date: e.target.value })} className={inputCls} data-testid="baby-mother-date" /></Field>
      <Field label="Time of birth"><input type="time" value={m.time} onChange={(e) => setM({ ...m, time: e.target.value })} className={inputCls} data-testid="baby-mother-time" /></Field>
      <Field label="Place of birth">
        <PlaceAutocompleteInput value={m.place} onChange={(v) => setM({ ...m, place: v })} onPick={({ name, timezone }) => setM({ ...m, place: name, timezone: timezone || m.timezone })} placeholder="Pune, India" testId="baby-mother-place" />
      </Field>
    </div>
  );
}

function InputForm({ form, setForm, useMother, setUseMother, mother, setMother, onRun, busy, disabled }) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); onRun(); }} className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-7 shadow-sm" data-testid="baby-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="From date"><input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className={inputCls} data-testid="baby-from-date" /></Field>
        <Field label="To date (max 60 days)"><input type="date" value={form.to_date} min={form.from_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className={inputCls} data-testid="baby-to-date" /></Field>
        <Field label="Hospital / delivery city">
          <PlaceAutocompleteInput value={form.place} onChange={(v) => setForm({ ...form, place: v })} onPick={({ name, timezone }) => setForm({ ...form, place: name, timezone: timezone || form.timezone })} placeholder="Mumbai, India" testId="baby-place" />
        </Field>
      </div>
      <label className="flex items-center gap-2 mt-5 text-sm text-[#3F2D1B] cursor-pointer">
        <input type="checkbox" checked={useMother} onChange={(e) => setUseMother(e.target.checked)} className="accent-amber-600 w-4 h-4" data-testid="baby-use-mother" />
        <Moon size={16} weight="duotone" className="text-pink-600" /> Also check the mother's chart (Chandrashtama, Tarabala, Chandrabala)
      </label>
      {useMother && <MotherFields m={mother} setM={setMother} />}
      <button type="submit" disabled={busy || disabled} className="btn-primary rounded-full px-7 py-3 mt-6 text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="baby-find-btn">
        <Sparkle size={16} weight="fill" /> {busy ? "Scanning every 10 minutes…" : "Find the best birth muhurat"}
      </button>
    </form>
  );
}

function Results({ r, place }) {
  const [slot, setSlot] = useState(null);
  return (
    <div className="space-y-10" data-testid="baby-results">
      <p className="text-xs text-[#3F2D1B]/60" data-testid="baby-scan-meta">Tap any best time to see the D1 chart for that moment. Scanned {r.slots_scanned.toLocaleString()} moments ({r.days_scanned} days × every {r.step_minutes} min) for {r.place}. All times are local ({r.timezone}). {r.baby.usable_slots.toLocaleString()} passed every classical rule.</p>
      <section className="space-y-4" data-testid="baby-section">
        <SectionTitle icon={Baby} title="Baby's birth muhurat" sub="Classical janma muhurat + lagna strength. Never during Grahan or Sutak, Amavasya, Krishna Chaturdashi, Gandanta, Sankranti, Bhadra or Dagdha tithi. Also needs benefics in or aspecting the lagna, the lagna lord in a kendra, trikona or the 11th, and no malefics in the 8th." />
        <DayStrip days={r.baby.per_day} />
        <ExclusionsPanel baby={r.baby} stepMinutes={r.step_minutes} />
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {r.baby.top_windows.slice(0, 9).map((w, i) => <WindowCard key={`${w.date}${w.from}`} w={w} rank={i} prefix="baby-window" onSlot={setSlot} />)}
        </div>
        {r.baby.top_windows.length === 0 && <p className="text-sm text-[#3F2D1B]/70">No moment in this range passed every classical rule. Try a wider date range.</p>}
      </section>
      {r.mother && <MotherSection m={r.mother} />}
      <section className="space-y-4" data-testid="summary-section">
        <SectionTitle icon={Sun} title={r.summary.considers_mother ? "Best for baby & mother together" : "Final recommendation"} sub={r.summary.considers_mother ? "Baby's chart (65%) + mother's Tarabala & Chandrabala (35%). Every pick avoids the mother's Chandrashtama." : "Top windows ranked by the baby's muhurat score."} />
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {r.summary.top.map((w, i) => <WindowCard key={`s${w.date}${w.from}`} w={w} rank={i} prefix="summary-window" showMother={r.summary.considers_mother} onSlot={setSlot} />)}
        </div>
      </section>
      <SlotChartDialog slot={slot} place={place} timezone={r.timezone} onClose={() => setSlot(null)} />
      <p className="text-[11px] text-[#3F2D1B]/55 flex gap-1.5"><Info size={12} className="mt-0.5 shrink-0" /> Astrological guidance only. The delivery date and time must always be decided by your doctor, based on medical safety for mother and baby.</p>
    </div>
  );
}

export default function BabyMuhuratPage() {
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const today = new Date();
  const [form, setForm] = useState({ from_date: iso(today), to_date: iso(new Date(today.getTime() + 14 * 864e5)), place: "", timezone: "Asia/Kolkata" });
  const [useMother, setUseMother] = useState(false);
  const [mother, setMother] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const loadStatus = () => api.get("/tools/baby-muhurat/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  useEffect(() => { if (user) loadStatus(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    if (!form.place.trim()) return toast.error("Enter the hospital / delivery city.");
    const span = (new Date(form.to_date) - new Date(form.from_date)) / 864e5 + 1;
    if (!(span >= 1)) return toast.error("The end date must be on or after the start date.");
    if (span > 60) return toast.error("Please choose a range of 60 days or less.");
    if (useMother && (!mother.date || !mother.time || !mother.place.trim())) return toast.error("Fill in all of the mother's birth details, or untick that option.");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/tools/baby-muhurat", { ...form, mother: useMother ? mother : null });
      setResult({ ...data, query_place: form.place });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not compute the muhurat.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell>
      <SEO title="Baby Delivery Muhurat · Best birth date & time · AstroKuberChat" description="Find the most auspicious date and time for a planned delivery, from classical janma muhurat rules, lagna strength and the mother's Chandrashtama." />
      <section className="max-w-6xl mx-auto px-5 lg:px-10 pt-10 pb-24" data-testid="baby-muhurat-page">
        <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-pink-600"><Baby size={14} weight="fill" /> Premium · {formatPrice(status?.price ?? 5000)}</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light mt-3 text-[#3F2D1B] leading-tight">Baby Delivery <span className="italic text-gold">Muhurat</span></h1>
        <p className="text-sm sm:text-base text-[#3F2D1B]/70 mt-4 max-w-3xl">Planning a C-section or induced delivery? Choose a date range. We check every 10-minute moment against classical birth-muhurat rules and the strength of the lagna, and can also check the mother's chart, to suggest the best date and time.</p>
        {!user ? (
          <div className="max-w-md mx-auto text-center rounded-2xl bg-white border border-amber-200 p-8 mt-10" data-testid="baby-signin-wall">
            <LockKey size={28} className="mx-auto text-amber-600" weight="duotone" />
            <h2 className="font-serif-display text-2xl mt-3 text-[#3F2D1B]">Sign in to use Baby Delivery Muhurat</h2>
            <button className="btn-primary rounded-full px-6 py-2.5 mt-5 text-sm" onClick={() => startGoogleSignIn(window.location.href)} data-testid="baby-signin-btn">Sign in</button>
          </div>
        ) : !status ? <div className="h-40 mt-8 rounded-2xl bg-amber-100/50 animate-pulse" /> : (
          <div className="space-y-8 mt-8">
            <InputForm form={form} setForm={setForm} useMother={useMother} setUseMother={setUseMother} mother={mother} setMother={setMother} onRun={run} busy={busy} disabled={!status.unlocked} />
            {!status.unlocked && (
              <PaywallGate tool={TOOL} toolLabel="Baby Delivery Muhurat" birthDetails={status.birth_details} variant="standalone" forWhat="for your account" footnote="One-time payment · unlimited date ranges for your account · included with VIP" pitch="Unlock the full analysis: the best birth windows from every 10-minute check, the mother's Chandrashtama and Tarabala calendar, and a combined recommendation for both." onUnlocked={loadStatus} />
            )}
            {result && <Results r={result} place={result.query_place || form.place} />}
          </div>
        )}
      </section>
    </PageShell>
  );
}
